import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Loader2, CheckCircle2, PenLine } from "lucide-react";
import { fetchReviews, submitReview } from "@/lib/site";

const EMPTY = { name: "", rating: 5, comment: "" };
const STAR_POSITIONS = [1, 2, 3, 4, 5];
const REVIEW_SUCCESS_MS = 4000;
const successAnim = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };
const cardReveal = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };

const initials = (name) =>
  name.trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

// Data fetching + list state for reviews.
const useReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const data = await fetchReviews();
      setReviews(data);
    } catch (err) {
      console.error("Failed to load reviews:", err);
      setLoadError("We couldn't load reviews right now. Please refresh to try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const prependReview = (review) => setReviews((r) => [review, ...r]);

  return { reviews, loading, loadError, prependReview };
};

export const Reviews = () => {
  const { reviews, loading, loadError, prependReview } = useReviews();

  return (
    <section id="leave-review" data-testid="reviews-section" className="py-20 lg:py-28 bg-cream scroll-mt-20">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-12">
          <p className="text-xs tracking-[0.2em] uppercase text-lawn font-bold mb-3">Customer reviews</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-ink">
            Had a job done? Leave us a review
          </h2>
          <p className="mt-4 text-ink-soft">
            We'd love to hear how we went. Your feedback helps other locals and keeps us doing our best work.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-10 items-start">
          <ReviewForm onCreated={prependReview} />
          <ReviewsList reviews={reviews} loading={loading} loadError={loadError} />
        </div>
      </div>
    </section>
  );
};

const ReviewForm = ({ onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const valid = form.name.trim() && form.comment.trim() && form.rating >= 1;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const created = await submitReview(form);
      onCreated(created);
      setForm(EMPTY);
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), REVIEW_SUCCESS_MS);
    } catch (err) {
      console.error("Failed to submit review:", err);
      setError("Sorry, we couldn't save your review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lg:col-span-2 bg-forest text-cream rounded-3xl p-7 sm:p-8 relative overflow-hidden">
      <div className="absolute inset-0 grain opacity-40" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-6">
          <PenLine className="w-5 h-5 text-lawn-light" />
          <h3 className="font-heading font-bold text-lg">Write a review</h3>
        </div>

        {submitted ? (
          <motion.div
            data-testid="review-success"
            {...successAnim}
            className="flex flex-col items-center text-center py-8"
          >
            <CheckCircle2 className="w-14 h-14 text-lawn-light mb-4" />
            <p className="font-heading font-bold text-xl">Thank you!</p>
            <p className="text-cream/70 mt-2">Your review is now live on the site.</p>
          </motion.div>
        ) : (
          <div className="space-y-5">
            <label className="block">
              <span className="block text-sm font-bold mb-2">Your name</span>
              <input
                data-testid="review-name-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Jane S."
                className="w-full rounded-xl border-2 border-cream/20 bg-cream/10 px-4 py-3 text-cream placeholder:text-cream/40 focus:border-lawn-light focus:outline-none transition-colors"
              />
            </label>

            <div>
              <span className="block text-sm font-bold mb-2">Your rating</span>
              <div className="flex gap-1.5" data-testid="review-rating">
                {STAR_POSITIONS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`review-star-${n}`}
                    onClick={() => setForm((f) => ({ ...f, rating: n }))}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-8 h-8 ${n <= form.rating ? "fill-lawn-light text-lawn-light" : "text-cream/30"}`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <label className="block">
              <span className="block text-sm font-bold mb-2">Your review</span>
              <textarea
                data-testid="review-comment-input"
                value={form.comment}
                onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
                rows={4}
                placeholder="Tell us how we went…"
                className="w-full rounded-xl border-2 border-cream/20 bg-cream/10 px-4 py-3 text-cream placeholder:text-cream/40 focus:border-lawn-light focus:outline-none transition-colors resize-none"
              />
            </label>

            {error && (
              <p data-testid="review-error" className="text-sm font-semibold text-red-200 bg-red-900/30 border border-red-400/40 rounded-xl px-4 py-3">
                {error}
              </p>
            )}

            <button
              data-testid="review-submit-button"
              onClick={handleSubmit}
              disabled={!valid || submitting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-lawn text-forest-dark px-8 py-3.5 text-base font-extrabold hover:bg-lawn-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> Posting…</>) : "Post My Review"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ReviewsList = ({ reviews, loading, loadError }) => {
  if (loading) {
    return (
      <div className="lg:col-span-3">
        <div className="flex items-center justify-center py-20 text-ink-soft" data-testid="reviews-loading">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="lg:col-span-3">
        <div data-testid="reviews-load-error" className="bg-white border border-line rounded-2xl p-8 text-center text-ink-soft">
          {loadError}
        </div>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="lg:col-span-3">
        <div data-testid="reviews-empty" className="bg-white border border-line rounded-2xl p-8 text-center text-ink-soft">
          No reviews yet — be the first to share your experience!
        </div>
      </div>
    );
  }

  return (
    <div className="lg:col-span-3">
      <div className="grid sm:grid-cols-2 gap-5">
        <AnimatePresence initial={false}>
          {reviews.map((r) => (
            <ReviewCard key={r.id} review={r} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

const ReviewCard = ({ review: r }) => (
  <motion.article
    data-testid={`customer-review-${r.id}`}
    layout
    {...cardReveal}
    className="bg-white border border-line rounded-2xl p-6"
  >
    <div className="flex items-center gap-3 mb-3">
      <span className="w-10 h-10 rounded-full bg-forest text-cream font-heading font-bold text-sm flex items-center justify-center shrink-0">
        {initials(r.name)}
      </span>
      <div>
        <p className="font-heading font-bold text-ink leading-tight">{r.name}</p>
        <p className="text-xs text-ink-soft">{formatDate(r.created_at)}</p>
      </div>
    </div>
    <div className="flex mb-3">
      {STAR_POSITIONS.map((p) => (
        <Star
          key={`${r.id}-star-${p}`}
          className={`w-4 h-4 ${p <= r.rating ? "fill-lawn text-lawn" : "text-line"}`}
        />
      ))}
    </div>
    <p className="text-ink leading-relaxed">{r.comment}</p>
  </motion.article>
);
